// ============================================================
// Réinitialisation de mot de passe — Console Super Admin (thème clair)
// ============================================================
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2, ShieldAlert } from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import FooterMentions from '../../components/common/FooterMentions';

export default function SuperAdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [form, setForm] = useState({ nouveau: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.nouveau.length < 8) {
      return setError('Le mot de passe doit faire au moins 8 caractères');
    }
    if (form.nouveau !== form.confirm) {
      return setError('Les deux mots de passe ne correspondent pas');
    }
    setLoading(true);
    try {
      await superAdminApi.post('/superadmin/auth/reset-password', {
        token, nouveauMotDePasse: form.nouveau,
      });
      setDone(true);
      setTimeout(() => navigate('/superadmin/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Lien invalide ou expiré. Refais une demande.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          {!token ? (
            <div className="text-center">
              <ShieldAlert size={40} className="mx-auto text-amber-500 mb-3" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Lien incomplet</h2>
              <p className="text-slate-500 text-sm">
                Ce lien de réinitialisation est invalide. Refais une demande depuis la connexion Super Admin.
              </p>
              <button
                onClick={() => navigate('/superadmin/login')}
                className="mt-5 w-full bg-slate-800 hover:bg-slate-900 text-white py-2.5 rounded-lg transition"
              >
                Retour à la connexion
              </button>
            </div>
          ) : done ? (
            <div className="text-center">
              <CheckCircle2 size={44} className="mx-auto text-emerald-500 mb-3" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Mot de passe réinitialisé</h2>
              <p className="text-slate-500 text-sm">Tu vas être redirigé vers la connexion…</p>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-slate-900 mb-1">Nouveau mot de passe</h2>
              <p className="text-slate-500 text-sm mb-6">Choisis un nouveau mot de passe (8 caractères minimum).</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-500 mb-1.5">Nouveau mot de passe</label>
                  <div className="relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={form.nouveau}
                      onChange={e => setForm({ ...form, nouveau: e.target.value })}
                      className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:border-blue-600"
                      placeholder="Min. 8 caractères"
                      required
                      autoFocus
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showPwd ? 'Masquer' : 'Afficher'}
                    >
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-1.5">Confirmer le mot de passe</label>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={form.confirm}
                    onChange={e => setForm({ ...form, confirm: e.target.value })}
                    className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-600"
                    placeholder="Re-saisis le mot de passe"
                    required
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition disabled:opacity-50"
                >
                  {loading ? 'Réinitialisation…' : 'Réinitialiser mon mot de passe'}
                </button>
              </form>
            </>
          )}
        </div>
        <FooterMentions variant="light" className="mt-6" />
      </div>
    </div>
  );
}
